import { CholeskyDecomposition } from '../math/CholeskyDecomposition';
import { DynamicRegimeDetector } from '../correlation/DynamicRegimeDetector';
import { FactorInteractionMatrix, FactorSet } from '../math/FactorInteractionMatrix';
import { TimeVaryingCorrelationModel } from '../correlation/TimeVaryingCorrelationModel';
import type { TimeVaryingCorrelation } from '@/types/timeVaryingCorrelation';

export interface MarketRegimeSettings {
  enabled: boolean;
  bullMarketProbability: number;
  bearMarketProbability: number;
  transitionProbability: number;
}

export interface InterestRateFactorSettings {
  enabled: boolean;
  rateGrowthCorrelation: number;
  rateDefensiveCorrelation: number;
  rateSensitivity: number;
}

export interface InflationFactorSettings {
  enabled: boolean;
  inflationGrowthCorrelation: number;
  inflationDefensiveCorrelation: number;
  inflationSensitivity: number;
}

export interface CreditEquitySettings {
  enabled: boolean;
  creditEquityCorrelation: number;
  creditSpreadSensitivity: number;
}

export interface CurrencyRiskSettings {
  enabled: boolean;
  currencyGrowthCorrelation: number;
  currencyDefensiveCorrelation: number;
  internationalExposure: number;
}

export interface VolatilityRegimeSettings {
  enabled: boolean;
  lowVolCorrelation: number;
  highVolCorrelation: number;
  regimeTransitionProbability: number;
}

export interface LiquidityStressSettings {
  enabled: boolean;
  liquidityGrowthCorrelation: number;
  liquidityDefensiveCorrelation: number;
  stressProbability: number;
}

export interface CorrelationSettings {
  growthDefensiveCorrelation: number;
  growthInflationCorrelation: number;
  defensiveInflationCorrelation: number;
  inflationCorrelation: number;
  sequenceRiskFactor: number;
  marketRegimeSettings: MarketRegimeSettings;
  interestRateSettings: InterestRateFactorSettings;
  inflationFactorSettings: InflationFactorSettings;
  creditEquitySettings: CreditEquitySettings;
  currencyRiskSettings: CurrencyRiskSettings;
  volatilityRegimeSettings: VolatilityRegimeSettings;
  liquidityStressSettings: LiquidityStressSettings;
}

export interface MarketReturns {
  growth: number;
  defensive: number;
  inflation: number;
  growthReturn: number;
  defensiveReturn: number;
  inflationRate: number;
  marketRegime?: string;
  interestRateFactor?: number;
  creditSpreadFactor?: number;
  currencyFactor?: number;
  volatilityRegime?: string;
  liquidityStress?: boolean;
}

export class CorrelationEngine {
  private seed?: number;
  private rng: () => number;
  private currentRegime: string;
  private regimeDuration: number = 0;
  private correlationMatrixCache = new Map<string, number[][]>();
  private regimeDetector: DynamicRegimeDetector;
  private factorInteractionMatrix: FactorInteractionMatrix;
  private timeVaryingModel: TimeVaryingCorrelationModel;
  private forecastCache = new Map<string, TimeVaryingCorrelation>();

  constructor(seed?: number) {
    this.seed = seed;
    this.rng = this.createSeededRNG(seed);
    this.regimeDetector = new DynamicRegimeDetector();
    this.factorInteractionMatrix = new FactorInteractionMatrix();
    this.timeVaryingModel = new TimeVaryingCorrelationModel();
    // Initialize regime from detector instead of hardcoding
    this.currentRegime = this.regimeDetector.getCurrentRegimeSync().current;
  }

  // Create deterministic random number generator for reproducibility
  private createSeededRNG(seed?: number): () => number {
    if (seed === undefined) {
      return Math.random;
    }
    
    // Use xorshift128+ for much better statistical properties
    let x = seed >>> 0; // Ensure 32-bit unsigned
    let y = (seed * 1103515245 + 12345) >>> 0; // Second seed component
    let z = (seed * 16807) >>> 0; // Third seed component  
    let w = (seed * 48271) >>> 0; // Fourth seed component
    
    return () => {
      const t = x ^ (x << 11);
      x = y;
      y = z;
      z = w;
      w = (w ^ (w >>> 19)) ^ (t ^ (t >>> 8));
      return (w >>> 0) / 0x100000000; // Convert to [0,1) range
    };
  }

  static getDefaultSettings(): CorrelationSettings {
    return {
      growthDefensiveCorrelation: -0.2,
      growthInflationCorrelation: -0.1,
      defensiveInflationCorrelation: 0.3,
      inflationCorrelation: 0.2,
      sequenceRiskFactor: 1.0,
      marketRegimeSettings: {
        enabled: true,
        bullMarketProbability: 0.3,
        bearMarketProbability: 0.15,
        transitionProbability: 0.1
      },
      interestRateSettings: {
        enabled: false,
        rateGrowthCorrelation: -0.3,
        rateDefensiveCorrelation: -0.8,
        rateSensitivity: 1.0
      },
      inflationFactorSettings: {
        enabled: false,
        inflationGrowthCorrelation: -0.2,
        inflationDefensiveCorrelation: -0.6,
        inflationSensitivity: 1.0
      },
      creditEquitySettings: {
        enabled: false,
        creditEquityCorrelation: 0.7,
        creditSpreadSensitivity: 1.0
      },
      currencyRiskSettings: {
        enabled: false,
        currencyGrowthCorrelation: 0.1,
        currencyDefensiveCorrelation: 0.0,
        internationalExposure: 0.3
      },
      volatilityRegimeSettings: {
        enabled: false,
        lowVolCorrelation: -0.2,
        highVolCorrelation: 0.6,
        regimeTransitionProbability: 0.05
      },
      liquidityStressSettings: {
        enabled: false,
        liquidityGrowthCorrelation: 0.8,
        liquidityDefensiveCorrelation: 0.4,
        stressProbability: 0.02
      }
    };
  }

  generateCorrelatedReturns(
    growthMean: number,
    defensiveMean: number,
    inflationMean: number,
    growthStdDev: number,
    defensiveStdDev: number,
    inflationStdDev: number,
    correlationSettings: CorrelationSettings
  ): MarketReturns {
    // Build correlation matrix with proper mathematical foundation
    const correlationMatrix = this.buildCorrelationMatrix(correlationSettings);
    const choleskyMatrix = this.choleskyDecomposition(correlationMatrix);
    
    // Generate independent normal random variables
    const independent = [
      this.normalRandom(0, 1),
      this.normalRandom(0, 1),
      this.normalRandom(0, 1)
    ];
    
    // Apply Cholesky transformation for correlation
    const correlated = [0, 0, 0];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j <= i; j++) {
        correlated[i] += choleskyMatrix[i][j] * independent[j];
      }
    }
    
    // Scale to desired means and volatilities
    const means = [growthMean, defensiveMean, inflationMean];
    const stdDevs = [growthStdDev, defensiveStdDev, inflationStdDev];
    
    const scaledReturns = correlated.map((value, i) => 
      means[i] + value * stdDevs[i]
    );
    
    // Apply factor shocks and regime effects
    const factorAdjustedReturns = this.applyFactorShocks(
      scaledReturns, 
      correlationSettings
    );
    
    // Determine market regime dynamically
    const marketRegime = this.determineMarketRegime(correlationSettings);
    
    // Generate additional factor values for reporting
    const interestRateFactor = this.normalRandom(0, 0.01);
    const creditSpreadFactor = this.normalRandom(0, 0.005);
    const currencyFactor = this.normalRandom(0, 0.02);
    const liquidityStress = this.rng() < (correlationSettings.liquidityStressSettings.enabled ? correlationSettings.liquidityStressSettings.stressProbability : 0);
    const isHighVolRegime = this.rng() < (correlationSettings.volatilityRegimeSettings.enabled ? correlationSettings.volatilityRegimeSettings.regimeTransitionProbability : 0);
    const volatilityRegime = isHighVolRegime ? 'high' : 'low';
    
    return {
      growth: factorAdjustedReturns[0],
      defensive: factorAdjustedReturns[1],
      inflation: factorAdjustedReturns[2],
      growthReturn: factorAdjustedReturns[0],
      defensiveReturn: factorAdjustedReturns[1],
      inflationRate: factorAdjustedReturns[2],
      marketRegime,
      interestRateFactor,
      creditSpreadFactor,
      currencyFactor,
      volatilityRegime,
      liquidityStress
    };
  }

  reset(): void {
    this.regimeDuration = 0;
    this.correlationMatrixCache.clear();
    this.regimeDetector.reset();
    // Reset regime from detector instead of hardcoding
    this.currentRegime = this.regimeDetector.getCurrentRegimeSync().current;
    // Reset RNG to initial state for reproducibility
    this.rng = this.createSeededRNG(this.seed);
  }

  // Apply sequence risk factor with factor interactions
  applySequenceRisk(
    returns: MarketReturns,
    year: number,
    retirementYear: number,
    settings: CorrelationSettings
  ): MarketReturns {
    const yearsFromRetirement = year - retirementYear;
    
    // Apply sequence risk in years -5 to +10 around retirement
    if (yearsFromRetirement >= -5 && yearsFromRetirement <= 10) {
      // Peak impact at retirement year, tapering off
      const sequenceWeight = 1 - Math.abs(yearsFromRetirement) / 10;
      const sequenceMultiplier = 1 + (settings.sequenceRiskFactor - 1) * sequenceWeight;
      
      // Get current regime for enhanced sequence risk modeling (using cached value)
      const regimeImpact = this.regimeDetector.getCachedRegimeImpact();
      
      // Build factor set for interaction analysis
      const factors = {
        interestRate: returns.interestRateFactor || 0,
        inflation: returns.inflationRate,
        creditSpread: returns.creditSpreadFactor || 0,
        volatility: regimeImpact > 1.2 ? 0.5 : 0,
        liquidity: returns.liquidityStress ? 1 : 0,
        currency: returns.currencyFactor || 0
      };
      
      // Calculate factor impacts with interactions
      const impacts = this.factorInteractionMatrix.calculateFactorImpacts(factors);
      
      // Amplify sequence risk during adverse regimes
      const volatilityIncrease = (sequenceMultiplier - 1) * 0.5 * regimeImpact;
      
      return {
        ...returns,
        growthReturn: returns.growthReturn * (1 + volatilityIncrease) + impacts.growthImpact * sequenceWeight,
        defensiveReturn: returns.defensiveReturn * (1 + volatilityIncrease * 0.5) + impacts.defensiveImpact * sequenceWeight,
        growth: returns.growth * (1 + volatilityIncrease) + impacts.growthImpact * sequenceWeight,
        defensive: returns.defensive * (1 + volatilityIncrease * 0.5) + impacts.defensiveImpact * sequenceWeight
      };
    }
    
    return returns;
  }

  // Build full correlation matrix from factor settings
  private buildCorrelationMatrix(settings: CorrelationSettings): number[][] {
    const n = 3; // Growth, Defensive, Inflation
    const matrix: number[][] = Array(n).fill(null).map(() => Array(n).fill(0));
    
    // Diagonal elements (self-correlation = 1)
    for (let i = 0; i < n; i++) {
      matrix[i][i] = 1.0;
    }
    
    // Fill correlation matrix
    matrix[0][1] = matrix[1][0] = this.validateCorrelation(settings.growthDefensiveCorrelation);
    matrix[0][2] = matrix[2][0] = this.validateCorrelation(settings.growthInflationCorrelation);
    matrix[1][2] = matrix[2][1] = this.validateCorrelation(settings.defensiveInflationCorrelation);
    
    // Apply factor adjustments to correlations
    this.applyFactorAdjustments(matrix, settings);
    
    // Ensure positive semi-definite property
    return this.ensurePositiveSemiDefinite(matrix);
  }

  // Cholesky decomposition using advanced mathematical library
  private choleskyDecomposition(matrix: number[][]): number[][] {
    const result = CholeskyDecomposition.decompose(matrix);
    
    if (!result.isValid && result.warnings.length > 0) {
      console.warn('⚠️ Cholesky decomposition warnings:', result.warnings);
    }
    
    return result.L;
  }

  // Dynamic market regime determination with DynamicRegimeDetector integration
  private determineMarketRegime(settings: CorrelationSettings): string {
    if (!settings.marketRegimeSettings.enabled) {
      return 'normal';
    }

    this.regimeDuration++;
    
    // Use DynamicRegimeDetector with synchronous cached regime
    const detectedRegime = this.regimeDetector.getCurrentRegimeSync();
    
    // Transition logic based on both market indicators and random transitions
    const transitionProb = settings.marketRegimeSettings.transitionProbability;
    
    if (this.rng() < transitionProb || this.regimeDuration > 12) { // Force check after 12 periods
      // Blend detected regime with stochastic transitions
      const rand = this.rng();
      
      // Higher probability for detected regime
      if (detectedRegime.confidence > 0.8) {
        // Map detected regime to internal regime
        if (detectedRegime.current === 'crisis' || detectedRegime.current === 'bear') {
          this.currentRegime = 'bear';
        } else if (detectedRegime.current === 'bull') {
          this.currentRegime = 'bull';
        } else {
          this.currentRegime = 'normal';
        }
      } else {
        // Use probability-based regime selection when confidence is lower
        const bullProb = settings.marketRegimeSettings.bullMarketProbability;
        const bearProb = settings.marketRegimeSettings.bearMarketProbability;
        
        if (rand < bullProb / (bullProb + bearProb + 0.55)) {
          this.currentRegime = 'bull';
        } else if (rand < (bullProb + bearProb) / (bullProb + bearProb + 0.55)) {
          this.currentRegime = 'bear';
        } else {
          this.currentRegime = 'normal';
        }
      }
      
      this.regimeDuration = 0;
    }
    
    return this.currentRegime;
  }

  // Apply factor shocks with interactions using FactorInteractionMatrix
  private applyFactorShocks(
    baseReturns: number[],
    settings: CorrelationSettings
  ): number[] {
    const [growth, defensive, inflation] = baseReturns;
    
    // Build factor set
    const factors: FactorSet = {
      interestRate: this.normalRandom(0, 0.01),
      inflation: inflation,
      creditSpread: this.normalRandom(0, 0.005),
      volatility: this.rng() < 0.1 ? 0.5 : 0,
      liquidity: this.rng() < 0.02 ? 1 : 0,
      currency: this.normalRandom(0, 0.02)
    };
    
    // Calculate comprehensive factor impacts including interactions
    const impacts = this.factorInteractionMatrix.calculateFactorImpacts(factors);
    
    // Apply impacts
    let adjustedReturns = [...baseReturns];
    adjustedReturns[0] += impacts.growthImpact;
    adjustedReturns[1] += impacts.defensiveImpact;
    
    return adjustedReturns;
  }

  // Apply factor adjustments to correlation matrix with FactorInteractionMatrix integration
  private applyFactorAdjustments(matrix: number[][], settings: CorrelationSettings): void {
    // Build current factor set from settings
    const factors = {
      interestRate: settings.interestRateSettings.enabled ? settings.interestRateSettings.rateSensitivity * 0.005 : 0,
      inflation: settings.inflationCorrelation * 0.025,
      creditSpread: settings.creditEquitySettings.enabled ? settings.creditEquitySettings.creditSpreadSensitivity * 0.004 : 0,
      volatility: this.rng() < 0.1 ? 0.5 : 0,
      liquidity: this.rng() < (settings.liquidityStressSettings.enabled ? settings.liquidityStressSettings.stressProbability : 0) ? 1 : 0,
      currency: settings.currencyRiskSettings.enabled ? settings.currencyRiskSettings.internationalExposure * 0.01 : 0
    };
    
    // Calculate factor impacts including interactions
    const impacts = this.factorInteractionMatrix.calculateFactorImpacts(factors);
    
    // Apply correlation clustering coefficient
    const clusteringCoefficient = this.factorInteractionMatrix.getCorrelationClusteringCoefficient(factors);
    
    // Adjust all correlations based on factor interaction impacts
    matrix[0][1] = matrix[1][0] = this.validateCorrelation(
      matrix[0][1] + impacts.correlationImpact + clusteringCoefficient
    );
    matrix[0][2] = matrix[2][0] = this.validateCorrelation(
      matrix[0][2] + impacts.correlationImpact * 0.8 + clusteringCoefficient * 0.7
    );
    matrix[1][2] = matrix[2][1] = this.validateCorrelation(
      matrix[1][2] + impacts.correlationImpact * 0.6 + clusteringCoefficient * 0.5
    );
    
    // Adjust for volatility regime separately
    if (settings.volatilityRegimeSettings.enabled) {
      const isHighVol = this.rng() < settings.volatilityRegimeSettings.regimeTransitionProbability;
      if (isHighVol) {
        const correlation = settings.volatilityRegimeSettings.highVolCorrelation;
        matrix[0][1] = matrix[1][0] = this.validateCorrelation(matrix[0][1] + correlation * 0.3);
        matrix[0][2] = matrix[2][0] = this.validateCorrelation(matrix[0][2] + correlation * 0.2);
        matrix[1][2] = matrix[2][1] = this.validateCorrelation(matrix[1][2] + correlation * 0.2);
      }
    }
  }

  // Ensure matrix is positive semi-definite
  private ensurePositiveSemiDefinite(matrix: number[][]): number[][] {
    // Simple approach: if any eigenvalue would be negative, regularize
    const n = matrix.length;
    const regularization = 0.001; // Small positive value
    
    // Add small amount to diagonal to ensure positive definiteness
    for (let i = 0; i < n; i++) {
      matrix[i][i] += regularization;
    }
    
    // Validate all correlations are within bounds
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i !== j) {
          matrix[i][j] = this.validateCorrelation(matrix[i][j]);
        }
      }
    }
    
    return matrix;
  }

  private normalRandom(mean: number, stdDev: number): number {
    // Box-Muller transform for normal distribution using seeded RNG
    const u = this.rng();
    const v = this.rng();
    const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    return mean + z * stdDev;
  }

  private validateCorrelation(value: number): number {
    // Ensure correlation stays within mathematically valid bounds
    if (!isFinite(value) || isNaN(value)) {
      console.warn('Invalid correlation value detected, using 0');
      return 0;
    }
    return Math.max(-1, Math.min(1, value));
  }

  /**
   * Calculate time-varying correlation with GARCH forecasting
   */
  calculateTimeVaryingCorrelation(
    returns1: number[],
    returns2: number[],
    dates: string[],
    options?: { windowSize?: number; includeForecasts?: boolean }
  ): TimeVaryingCorrelation {
    // Generate cache key
    const cacheKey = `${returns1.length}_${returns2.length}_${dates[dates.length - 1]}`;
    
    // Check cache
    const cached = this.forecastCache.get(cacheKey);
    if (cached && cached.current_date === dates[dates.length - 1]) {
      return cached;
    }
    
    // Calculate new forecast
    const forecast = this.timeVaryingModel.calculateTimeVaryingCorrelation(
      returns1,
      returns2,
      dates,
      options
    );
    
    // Cache result
    this.forecastCache.set(cacheKey, forecast);
    
    // Clean old cache entries (keep last 10)
    if (this.forecastCache.size > 10) {
      const firstKey = this.forecastCache.keys().next().value;
      if (firstKey) {
        this.forecastCache.delete(firstKey);
      }
    }
    
    return forecast;
  }

  /**
   * Clear forecast cache
   */
  clearForecastCache(): void {
    this.forecastCache.clear();
  }

  /**
   * Get regime detector for external use
   */
  getRegimeDetector(): DynamicRegimeDetector {
    return this.regimeDetector;
  }
}
